import React from "react";
import { useTranslation } from "react-i18next";
import { addYears, endOfDay, startOfDay } from "date-fns";
import ShowChartIcon from "@icons/myicons/sidebar/investments.svg?react";
import { toCurrency } from "@helpers";
import { MiniCardChart } from "@components/Chart";
import { NoAccountsMiniCard } from "@components/Card";
import { usePreparePerformanceChart } from "@features/dashboard/hooks";
import { useDashboardSelectedGroupsContext } from "@features/dashboard/DashboardSelectedGroupProvider.tsx";
import { LoadingPlaceHolder } from "@rentablo/components";

export interface InvestmentsCardProps {
  title?: string;
  investmentsCount: number;
  currency: string;
  onAddAccountsClick: () => void;
  onClick: () => void;
}

export const InvestmentsCard = ({
  title,
  investmentsCount,
  currency,
  onAddAccountsClick,
  onClick,
}: InvestmentsCardProps) => {
  const { t } = useTranslation();
  const { groupedAccountIds, isFetching: areAccountsLoading } =
    useDashboardSelectedGroupsContext();
  const accountIds = [
    ...groupedAccountIds.cashIds,
    ...groupedAccountIds.creditCardIds,
    ...groupedAccountIds.depotIds,
  ];

  const defaultStartDate = startOfDay(addYears(new Date(), -1));
  const defaultEndDate = endOfDay(new Date());
  const performanceChartData = usePreparePerformanceChart(
    accountIds,
    defaultStartDate,
    defaultEndDate
  );
  const portfolioValue = performanceChartData.currentPortfolioValue ?? 0;
  const changeYTD = performanceChartData.rawData?.changeYTD?.performance ?? 0;
  const chartData = performanceChartData.chartData;

  if (performanceChartData.isFetching || areAccountsLoading) {
    return <LoadingPlaceHolder />;
  }
  return (
    <>
      {investmentsCount === 0 && (
        <NoAccountsMiniCard
          onClick={onAddAccountsClick}
          icon={<ShowChartIcon width="24px" height="24px" />}
          title={t("domains.investmentsCard.noAccountsTitle")}
          subtitle={t("domains.investmentsCard.noAccountsSubtitle")}
          buttonText={t("domains.investmentsCard.noAccountsButtonText")}
        />
      )}
      {investmentsCount > 0 && chartData && chartData?.length > 0 && (
        <MiniCardChart
          onClick={onClick}
          icon={<ShowChartIcon width="24px" height="24px" />}
          data={chartData || []}
          headline={toCurrency(portfolioValue, currency)}
          title={title || t("domains.investmentsCard.title")}
          subtitle={t("domains.investmentsCard.subtitle", {
            count: investmentsCount,
          })}
          percent={changeYTD}
          percentPrefix={`${t("domains.investmentsCard.percentPrefix")}`}
        />
      )}
    </>
  );
};
